import type { ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePageHeaderContext } from "@/contexts/PageHeaderContext";
import { cn } from "@/lib/utils";

/**
 * The page's entity actions (`useContextActions`) as a menu in the context
 * header. `leading` adds host items above them, e.g. the panel's "Open as
 * page". Renders nothing when there is nothing to show.
 */
export function ContextActionsMenu({
  leading,
  className,
  testId = "context-actions-menu",
}: {
  leading?: ReactNode;
  className?: string;
  testId?: string;
}) {
  const { actions } = usePageHeaderContext();
  if (actions.length === 0 && !leading) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-7 w-7 shrink-0 text-muted-foreground", className)}
          title="Actions"
          aria-label="Actions"
          data-testid={testId}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {leading}
        {leading && actions.length > 0 && <DropdownMenuSeparator />}
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.key}
            offlineEdit={action.offlineEdit}
            disabled={action.disabled}
            onSelect={action.onSelect}
            className={cn(
              "gap-2",
              action.destructive && "text-destructive focus:text-destructive",
            )}
            data-testid={action.testId}
          >
            {action.icon && <action.icon className="h-4 w-4" />}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
